import { parseImageMetadata } from './exif.js';
import type { ImageMetadata } from '../types/metadata.js';

/**
 * Magic-byte sniffing and header parsing.
 *
 * The client `Content-Type` is never trusted: the browser upload path and the
 * optional server fallback both call this module first, so a `.jpg` that is
 * really an SVG, an HTML page, or a truncated header is rejected before any
 * decoder sees it.
 */

export type SniffedFormat =
  | 'jpeg'
  | 'png'
  | 'webp'
  | 'bmp'
  | 'gif'
  | 'heic'
  | 'svg'
  | 'html'
  | 'unknown';

export interface SniffResult {
  format: SniffedFormat;
  /** MIME type derived from the bytes, never from the client header. */
  mimeType: string;
  /** True when the format is safe and supported for document photos. */
  supported: boolean;
  width?: number;
  height?: number;
  animated?: boolean;
  hasExif?: boolean;
  /**
   * Parsed container metadata (EXIF/JFIF/ICC). Present whenever the head of the
   * file was available for parsing, even when it contains no metadata at all —
   * an empty result and "not parsed" are different states for the checks.
   */
  metadata?: ImageMetadata;
  /** Set when the container is recognised but malformed/truncated. */
  malformed?: boolean;
  reason?: string;
}

const MIME_BY_FORMAT: Record<SniffedFormat, string> = {
  jpeg: 'image/jpeg',
  png: 'image/png',
  webp: 'image/webp',
  bmp: 'image/bmp',
  gif: 'image/gif',
  heic: 'image/heic',
  svg: 'image/svg+xml',
  html: 'text/html',
  unknown: 'application/octet-stream',
};

const startsWith = (bytes: Uint8Array, signature: number[], offset = 0): boolean => {
  if (bytes.length < offset + signature.length) return false;
  for (let i = 0; i < signature.length; i += 1) {
    if (bytes[offset + i] !== signature[i]) return false;
  }
  return true;
};

const asciiAt = (bytes: Uint8Array, offset: number, length: number): string => {
  let out = '';
  for (let i = 0; i < length; i += 1) {
    const value = bytes[offset + i];
    if (value === undefined) return out;
    out += String.fromCharCode(value);
  }
  return out;
};

const readUint16BE = (bytes: Uint8Array, offset: number): number =>
  ((bytes[offset] as number) << 8) | (bytes[offset + 1] as number);

const readUint16LE = (bytes: Uint8Array, offset: number): number =>
  ((bytes[offset + 1] as number) << 8) | (bytes[offset] as number);

const readUint24LE = (bytes: Uint8Array, offset: number): number =>
  ((bytes[offset + 2] as number) << 16) |
  ((bytes[offset + 1] as number) << 8) |
  (bytes[offset] as number);

const readUint32BE = (bytes: Uint8Array, offset: number): number =>
  (((bytes[offset] as number) << 24) |
    ((bytes[offset + 1] as number) << 16) |
    ((bytes[offset + 2] as number) << 8) |
    (bytes[offset + 3] as number)) >>>
  0;

/**
 * True when the buffer looks like SVG or HTML **and is not a known binary image**.
 *
 * The container signatures are checked first on purpose. Real photographs carry
 * arbitrary metadata in their first bytes — C2PA Content Credentials (`caBX` /
 * JUMBF) embed an SVG thumbnail, XMP packets embed XML — and an earlier version
 * of this function scanned the first 512 bytes for the substring `<svg` before
 * looking at the magic numbers, so every C2PA-signed photo was rejected as a
 * vector file. A binary image is never markup, whatever its metadata contains.
 */
export const looksLikeMarkup = (bytes: Uint8Array): boolean => {
  if (hasBinaryImageSignature(bytes)) return false;
  const head = asciiAt(bytes, 0, Math.min(bytes.length, 512)).toLowerCase();
  // `asciiAt` maps bytes to latin1, so a UTF-8 BOM arrives as `ï»¿`.
  const trimmed = head.replace(/^(\uFEFF|\u00ef\u00bb\u00bf)/, '').trimStart();
  return (
    trimmed.startsWith('<?xml') ||
    trimmed.startsWith('<svg') ||
    trimmed.startsWith('<!doctype html') ||
    trimmed.startsWith('<html') ||
    trimmed.includes('<svg')
  );
};

/** Container magic numbers of every format the validator can decode. */
const hasBinaryImageSignature = (bytes: Uint8Array): boolean =>
  startsWith(bytes, [0xff, 0xd8, 0xff]) || // JPEG
  startsWith(bytes, [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a]) || // PNG
  startsWith(bytes, [0x47, 0x49, 0x46, 0x38]) || // GIF87a/89a
  startsWith(bytes, [0x42, 0x4d]) || // BMP
  startsWith(bytes, [0x52, 0x49, 0x46, 0x46]) || // RIFF (WebP)
  isIsoBmff(bytes); // HEIC/AVIF: `....ftyp`

/** ISO base media file format: a box size, then `ftyp` at offset 4. */
const isIsoBmff = (bytes: Uint8Array): boolean =>
  bytes.length > 8 &&
  bytes[4] === 0x66 &&
  bytes[5] === 0x74 &&
  bytes[6] === 0x79 &&
  bytes[7] === 0x70;

/**
 * Sniffs the container and reads its real dimensions from the header.
 *
 * Returns `supported: false` (never throws) for anything the validator must not
 * accept, including SVG/HTML, animated images, and truncated headers.
 */
export const sniffImage = (bytes: Uint8Array): SniffResult => {
  const withMetadata = (result: SniffResult): SniffResult => ({
    ...result,
    metadata: parseImageMetadata(bytes, result.format),
  });

  if (bytes.length < 12) {
    return withMetadata({
      format: 'unknown',
      mimeType: MIME_BY_FORMAT.unknown,
      supported: false,
      malformed: true,
      reason: 'Файл слишком короткий, чтобы быть изображением.',
    });
  }

  if (looksLikeMarkup(bytes)) {
    return withMetadata({
      format: 'svg',
      mimeType: MIME_BY_FORMAT.svg,
      supported: false,
      reason: 'SVG/HTML не принимаются: векторная и разметочная графика запрещена.',
    });
  }

  // JPEG
  if (startsWith(bytes, [0xff, 0xd8, 0xff])) {
    const parsed = parseJpeg(bytes);
    return withMetadata({
      format: 'jpeg',
      mimeType: MIME_BY_FORMAT.jpeg,
      supported: true,
      width: parsed.width,
      height: parsed.height,
      hasExif: parsed.hasExif,
      malformed: parsed.width === undefined,
      ...(parsed.width === undefined
        ? { reason: 'Не удалось прочитать размеры JPEG (повреждённый заголовок).' }
        : {}),
    });
  }

  // PNG
  if (startsWith(bytes, [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a])) {
    const width = readUint32BE(bytes, 16);
    const height = readUint32BE(bytes, 20);
    return withMetadata({
      format: 'png',
      mimeType: MIME_BY_FORMAT.png,
      supported: true,
      width,
      height,
      animated: false,
      malformed: width === 0 || height === 0,
      ...(width === 0 || height === 0 ? { reason: 'PNG с нулевыми размерами.' } : {}),
    });
  }

  // WEBP
  if (asciiAt(bytes, 0, 4) === 'RIFF' && asciiAt(bytes, 8, 4) === 'WEBP') {
    const parsed = parseWebp(bytes);
    return withMetadata({
      format: 'webp',
      mimeType: MIME_BY_FORMAT.webp,
      supported: true,
      width: parsed.width,
      height: parsed.height,
      animated: parsed.animated,
      malformed: parsed.width === undefined,
      ...(parsed.width === undefined ? { reason: 'Не удалось прочитать размеры WEBP.' } : {}),
    });
  }

  // BMP
  if (asciiAt(bytes, 0, 2) === 'BM' && bytes.length >= 26) {
    return withMetadata({
      format: 'bmp',
      mimeType: MIME_BY_FORMAT.bmp,
      supported: true,
      width: Math.abs(readUint32BE(bytes, 18) >> 0),
      height: Math.abs(readUint32BE(bytes, 22) >> 0),
    });
  }

  // GIF (always rejected: animated and palette-limited)
  if (asciiAt(bytes, 0, 4) === 'GIF8') {
    return withMetadata({
      format: 'gif',
      mimeType: MIME_BY_FORMAT.gif,
      supported: false,
      width: readUint16LE(bytes, 6),
      height: readUint16LE(bytes, 8),
      animated: true,
      reason: 'GIF не принимается для фотографий на документы.',
    });
  }

  // HEIC/HEIF (ISO-BMFF with ftypheic/ftypmif1)
  if (asciiAt(bytes, 4, 4) === 'ftyp') {
    const brand = asciiAt(bytes, 8, 4).toLowerCase();
    if (brand.startsWith('hei') || brand.startsWith('mif') || brand.startsWith('msf')) {
      return {
        format: 'heic',
        mimeType: MIME_BY_FORMAT.heic,
        supported: false,
        reason: 'HEIC/HEIF нужно сначала сконвертировать в JPEG.',
      };
    }
  }

  return withMetadata({
    format: 'unknown',
    mimeType: MIME_BY_FORMAT.unknown,
    supported: false,
    reason: 'Формат файла не распознан по сигнатуре.',
  });
};

interface JpegHeader {
  width?: number;
  height?: number;
  hasExif: boolean;
}

const parseJpeg = (bytes: Uint8Array): JpegHeader => {
  let offset = 2;
  let hasExif = false;
  while (offset + 9 < bytes.length) {
    if (bytes[offset] !== 0xff) {
      offset += 1;
      continue;
    }
    const marker = bytes[offset + 1] as number;
    if (marker === 0xd8 || marker === 0x01 || (marker >= 0xd0 && marker <= 0xd7)) {
      offset += 2;
      continue;
    }
    const length = readUint16BE(bytes, offset + 2);
    if (length < 2) return { hasExif };
    if (marker === 0xe1 && asciiAt(bytes, offset + 4, 4) === 'Exif') {
      hasExif = true;
    }
    // SOF0..SOF15 except DHT (0xc4), JPG (0xc8), DAC (0xcc)
    const isSof =
      marker >= 0xc0 && marker <= 0xcf && marker !== 0xc4 && marker !== 0xc8 && marker !== 0xcc;
    if (isSof) {
      const height = readUint16BE(bytes, offset + 5);
      const width = readUint16BE(bytes, offset + 7);
      return { width, height, hasExif };
    }
    if (marker === 0xda) break; // start of scan: no SOF found
    offset += 2 + length;
  }
  return { hasExif };
};

const parseWebp = (
  bytes: Uint8Array,
): { width?: number; height?: number; animated: boolean } => {
  const chunk = asciiAt(bytes, 12, 4);
  if (chunk === 'VP8X' && bytes.length >= 30) {
    const flags = bytes[20] as number;
    const animated = (flags & 0x02) !== 0;
    const width = readUint24LE(bytes, 24) + 1;
    const height = readUint24LE(bytes, 27) + 1;
    return { width, height, animated };
  }
  if (chunk === 'VP8L' && bytes.length >= 25) {
    const b0 = bytes[21] as number;
    const b1 = bytes[22] as number;
    const b2 = bytes[23] as number;
    const b3 = bytes[24] as number;
    const width = 1 + (((b1 & 0x3f) << 8) | b0);
    const height = 1 + (((b3 & 0x0f) << 10) | (b2 << 2) | ((b1 & 0xc0) >> 6));
    return { width, height, animated: false };
  }
  if (chunk === 'VP8 ' && bytes.length >= 30) {
    const width = readUint16LE(bytes, 26) & 0x3fff;
    const height = readUint16LE(bytes, 28) & 0x3fff;
    return { width, height, animated: false };
  }
  return { animated: false };
};
